import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';

export type TriStateMode = 'unchanged' | 'set' | 'clear';

interface TriStateOption {
  value: TriStateMode;
  labelKey: string;
}

export interface TriStatePatchOperation {
  op: 'set' | 'clear';
  value?: unknown;
}

@Component({
  selector: 'zag-tri-state-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ZagButton],
  template: `
    <div class="zag-tri-state-field">
      @if (labelKey()) {
        <span class="zag-tri-state-field__label">{{ labelKey() | translate }}</span>
      }
      @if (showMode()) {
        <div class="zag-tri-state-field__switch" role="group" [attr.aria-label]="'bulkEdit.fieldEditMode' | translate">
          @for (opt of options(); track opt.value) {
            <button
              zagButton="outlined"
              zagSize="sm"
              type="button"
              class="zag-tri-state-field__option"
              [class.zag-tri-state-field__option--active]="mode() === opt.value"
              [attr.aria-pressed]="mode() === opt.value"
              (click)="setMode(opt.value)"
            >
              {{ opt.labelKey | translate }}
            </button>
          }
        </div>
      }
      <div
        class="zag-tri-state-field__content"
        [class.zag-tri-state-field__content--boxed]="showMode()"
        [attr.inert]="showMode() && mode() !== 'set' ? '' : null"
      >
        <ng-content />
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .zag-tri-state-field {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs);
      }

      .zag-tri-state-field__label {
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-medium);
        color: var(--text-color-secondary);
      }

      .zag-tri-state-field__switch {
        display: inline-flex;
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-sm);
        overflow: hidden;
        align-self: flex-start;
      }

      .zag-tri-state-field__option {
        padding: var(--spacing-2xs) var(--spacing-sm);
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-medium);
        border-right: 1px solid var(--border-color-default);
        color: var(--text-color-secondary);
      }

      .zag-tri-state-field__option:last-child {
        border-right: none;
      }

      .zag-tri-state-field__option--active {
        background-color: var(--color-primary);
        color: var(--color-neutral-0);
      }

      .zag-tri-state-field__option:focus-visible {
        outline: 2px solid var(--color-primary);
        outline-offset: -2px;
      }

      .zag-tri-state-field__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .zag-tri-state-field__content--boxed {
        padding: var(--spacing-lg);
        background: var(--surface-color-subtle);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
      }

      .zag-tri-state-field__content[inert] {
        display: none;
      }
    `,
  ],
})
export class TriStateFieldComponent {
  readonly labelKey = input<string>('');
  readonly showMode = input(true);
  readonly allowClear = input(true);
  readonly value = input<unknown>(undefined);
  readonly mode = model<TriStateMode>('unchanged');
  readonly patchChange = output<TriStatePatchOperation | undefined>();

  private readonly allOptions: ReadonlyArray<TriStateOption> = [
    { value: 'unchanged', labelKey: 'bulkEdit.unchanged' },
    { value: 'set', labelKey: 'bulkEdit.set' },
    { value: 'clear', labelKey: 'bulkEdit.clear' },
  ];

  protected readonly options = computed(() => (this.allowClear() ? this.allOptions : this.allOptions.slice(0, 2)));

  protected setMode(nextMode: TriStateMode): void {
    this.mode.set(nextMode);
    if (nextMode === 'unchanged') {
      this.patchChange.emit(undefined);
      return;
    }

    this.patchChange.emit(nextMode === 'set' ? { op: 'set', value: this.value() } : { op: 'clear' });
  }
}
